import React, { useState } from "react";
import styled from "@mui/material/styles/styled";
import Switch from "@mui/material/Switch";
import "./ReportsMain.css";

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: 'rgba(87, 42, 241, 1)',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#fff',
      border: '6px solid rgba(87, 42, 241, 0.3)',
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
    backgroundColor: '#fff',
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'dark' ? '#39393D' : '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const ReportsMain = () => {
  const [activeContent, setActiveContent] = useState("CSM Reports Access");
  const [csmAccessEnabled, setCsmAccessEnabled] = useState(false);
  const [euroAsDefault, setEuroAsDefault] = useState(false);
  const [reportExportEnabled, setReportExportEnabled] = useState(false);

  const handleLinkClick = (content) => {
    setActiveContent(content);
  };

  const renderContent = () => {
    switch (activeContent) {
      case "CSM Reports Access":
        return (
          <div>
            <h2 className="centered-title">CSM Reports Access</h2>
            <p>
              This toggle controls whether Client Success Managers (CSMs) can access detailed reporting features. When enabled, CSMs can view comprehensive client health metrics, retention trends, and performance analytics to better manage their client relationships.
            </p>
            <p>
              When disabled, CSM access is restricted to basic client information only, which may be preferred for organizations with strict data access policies or where report analysis is centralized with management.
            </p>
            <div className="toggle-container">
              <label className="switch-container">
                <IOSSwitch
                  checked={csmAccessEnabled}
                  onChange={() => setCsmAccessEnabled(!csmAccessEnabled)}
                  name="csmAccess"
                />
                <span className="toggle-label">CSM Reports Access</span>
              </label>
            </div>
          </div>
        );
      case "Currency Selection":
        return (
          <div>
            <h2 className="centered-title">Currency Selection</h2>
            <p>
              This setting determines the default currency displayed throughout all reports and dashboards. By default, all monetary values are shown in US Dollars (USD).
            </p>
            <p>
              When toggled, the system will display all financial metrics including client value, revenue projections, and retention impact calculations in Euros (€) instead.
            </p>
            <div className="toggle-container">
              <label className="switch-container">
                <IOSSwitch
                  checked={euroAsDefault}
                  onChange={() => setEuroAsDefault(!euroAsDefault)}
                  name="euroAsDefault"
                />
                <span className="toggle-label">Euro as Default Currency</span>
              </label>
            </div>
          </div>
        );
      case "Report Exporting":
        return (
          <div>
            <h2 className="centered-title">Report Exporting</h2>
            <p>
              By default, reports are exportable as PDFs. This toggle disables the ability to export reports as PDF documents. When this setting is off (default), users can generate professional PDF reports of dashboards, client health summaries, AI call summaries, and retention analytics for sharing with stakeholders, presenting in meetings, or archiving.
            </p>
            <p>
              When enabled, reports remain viewable only within the platform, which may be preferred for organizations with stricter data security requirements or to prevent unauthorized distribution of sensitive client information.
            </p>
            <div className="toggle-container">
              <label className="switch-container">
                <IOSSwitch
                  checked={reportExportEnabled}
                  onChange={() => setReportExportEnabled(!reportExportEnabled)}
                  name="reportExportEnabled"
                />
                <span className="toggle-label">Turn Off Report Exporting</span>
              </label>
            </div>
          </div>
        );
      default:
        return (
          <div className="empty-state">
            Select a report setting to view details
          </div>
        );
    }
  };

  return (
    <>
      <div className="bannerSoon">This feature will be available soon.</div>
      <div className="reports-wrapper">
        <div className="reports-container reports-settings">
          <h1>Report Settings</h1>
          <p>
            The settings in this section apply globally to all reporting features across your ClientBloom account. These configurations establish the default behavior for all reports and cannot be overridden by individual team members.
          </p>
          <p>
            These global settings ensure consistency in your reporting processes while providing the foundation for future enhancements to ClientBloom's analytics capabilities. As we expand our reporting features with more advanced visualizations and predictive analytics, these core settings will continue to govern how those insights are delivered to your team.
          </p>

          <h2>Customize Report Settings</h2>
          <ul className="metric-buttons">
            <li>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleLinkClick("CSM Reports Access");
                }}
                className={activeContent === "CSM Reports Access" ? "active" : ""}
              >
                CSM Reports Access
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleLinkClick("Currency Selection");
                }}
                className={activeContent === "Currency Selection" ? "active" : ""}
              >
                Currency Selection
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleLinkClick("Report Exporting");
                }}
                className={activeContent === "Report Exporting" ? "active" : ""}
              >
                Report Exporting
              </a>
            </li>
          </ul>
        </div>

        <div className="reports-container csm-access">
          {renderContent()}
        </div>
      </div>
    </>
  );
};

export default ReportsMain;